import { useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useUIStore } from '@/stores';
import type { PageKey } from '@/stores/uiStore';

const HORIZON_DAYS = 180;

interface CalendarNavigationProps {
  pageKey: PageKey;
  mode: 'weekly' | 'monthly';
  onNavigateBeyondHorizon?: (targetDate: Date) => void;
}

export function CalendarNavigation({ pageKey, mode, onNavigateBeyondHorizon }: CalendarNavigationProps) {
  const { calendarOffset, navigateCalendar, resetCalendarOffset } = useUIStore();
  const offset = calendarOffset[pageKey][mode];

  // Calculate the target date based on current offset
  const getTargetDate = useCallback((newOffset: number): Date => {
    const now = new Date();
    if (mode === 'weekly') {
      const currentDay = now.getDay();
      const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() + mondayOffset + newOffset * 7);
      return weekStart;
    } else {
      return new Date(now.getFullYear(), now.getMonth() + newOffset, 1);
    }
  }, [mode]);

  // Check if a date is beyond the 180-day horizon
  const isBeyondHorizon = useCallback((targetDate: Date): boolean => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const horizonDate = new Date(now);
    horizonDate.setDate(horizonDate.getDate() + HORIZON_DAYS);
    return targetDate > horizonDate;
  }, []);

  const handleNavigate = useCallback((direction: 1 | -1) => {
    const newOffset = offset + direction;
    const targetDate = getTargetDate(newOffset);

    // Check if navigating beyond horizon
    if (direction > 0 && isBeyondHorizon(targetDate) && onNavigateBeyondHorizon) {
      onNavigateBeyondHorizon(targetDate);
    }

    navigateCalendar(pageKey, mode, direction);
  }, [offset, getTargetDate, isBeyondHorizon, onNavigateBeyondHorizon, navigateCalendar, pageKey, mode]);

  const dateLabel = useMemo(() => {
    const now = new Date();

    if (mode === 'weekly') {
      // Calculate week start (Monday) based on offset
      const currentDay = now.getDay();
      const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() + mondayOffset + offset * 7);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const startMonth = weekStart.toLocaleDateString('en-US', { month: 'short' });
      const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' });
      const year = weekEnd.getFullYear();

      if (startMonth === endMonth) {
        return `${startMonth} ${weekStart.getDate()}-${weekEnd.getDate()}, ${year}`;
      } else {
        return `${startMonth} ${weekStart.getDate()} - ${endMonth} ${weekEnd.getDate()}, ${year}`;
      }
    } else {
      // Monthly view
      const targetDate = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return targetDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  }, [mode, offset]);

  const isCurrentPeriod = offset === 0;

  return (
    <div className="flex items-center gap-2">
      {/* Reset button - only show when not on current period */}
      {!isCurrentPeriod && (
        <button
          onClick={() => resetCalendarOffset(pageKey, mode)}
          className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title={mode === 'weekly' ? 'Reset to current week' : 'Reset to current month'}
        >
          Reset
        </button>
      )}

      <button
        onClick={() => handleNavigate(-1)}
        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        title={mode === 'weekly' ? 'Previous week' : 'Previous month'}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <button
        onClick={() => resetCalendarOffset(pageKey, mode)}
        className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
          isCurrentPeriod
            ? 'text-gray-900 bg-gray-100'
            : 'text-primary-600 hover:bg-primary-50'
        }`}
        title={isCurrentPeriod ? 'Current period' : 'Go to today'}
      >
        {dateLabel}
      </button>

      <button
        onClick={() => handleNavigate(1)}
        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        title={mode === 'weekly' ? 'Next week' : 'Next month'}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
